import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const FactoryContext = createContext(null);

/** Per-user storage key — see /lib/api.js for the matching interceptor. */
const keyFor = (uid) => uid ? `c2p_factory_${uid}` : '';

export function FactoryProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.user_id || '';
  const lastUidRef = useRef(uid);

  const [factories, setFactories] = useState([]);
  const [activeFactoryId, setActiveFactoryIdState] = useState(() => {
    try {
      const initialUid = uid || localStorage.getItem('c2p_active_uid') || '';
      const k = keyFor(initialUid);
      return (k && localStorage.getItem(k)) || '';
    } catch { return ''; }
  });
  const [loading, setLoading] = useState(false);

  const setActiveFactoryId = useCallback((fid) => {
    try {
      const k = keyFor(uid);
      if (!k) return; // No user → don't persist
      if (fid) localStorage.setItem(k, fid);
      else localStorage.removeItem(k);
    } catch (e) { console.debug('[caught]', e); }
    setActiveFactoryIdState(fid || '');
    try { window.dispatchEvent(new CustomEvent('factory-changed', { detail: { factory_id: fid } })); } catch (e) { console.debug('[caught]', e); }
  }, [uid]);

  const refresh = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const r = await api.get('/factories');
      const rows = (r.data || []).filter(f => f.status !== 'archived');
      setFactories(rows);
      // Re-read the current selection from per-user key (it may have changed)
      let stored = '';
      try { stored = localStorage.getItem(keyFor(uid)) || ''; } catch (e) { console.debug('[caught]', e); }
      const isAccessible = rows.some(f => f.id === stored);
      if (!stored || !isAccessible) {
        const def = rows.find(f => f.is_default) || rows[0];
        if (def) setActiveFactoryId(def.id);
        else setActiveFactoryIdState('');
      } else if (stored !== activeFactoryId) {
        setActiveFactoryIdState(stored);
      }
    } catch (err) {
      // Pre-Phase-4 tenants without /factories will fail silently — fine.
      console.warn('factories refresh failed', err?.response?.status);
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, setActiveFactoryId]);

  // When user identity changes (login / logout / switch account on shared device),
  // reset state to the new user's per-user value before refetching.
  useEffect(() => {
    if (lastUidRef.current !== uid) {
      lastUidRef.current = uid;
      try {
        const k = keyFor(uid);
        const next = (k && localStorage.getItem(k)) || '';
        setActiveFactoryIdState(next);
      } catch { setActiveFactoryIdState(''); }
      setFactories([]);
    }
    if (uid) refresh();
  }, [uid, refresh]);

  const activeFactory = useMemo(
    () => factories.find(f => f.id === activeFactoryId) || null,
    [factories, activeFactoryId]
  );

  // Memoise the context value so consumers only re-render when a field they
  // rely on actually changes, not on every FactoryProvider render.
  const value = useMemo(
    () => ({ factories, activeFactory, activeFactoryId, setActiveFactoryId, refresh, loading }),
    [factories, activeFactory, activeFactoryId, setActiveFactoryId, refresh, loading]
  );

  return (
    <FactoryContext.Provider value={value}>
      {children}
    </FactoryContext.Provider>
  );
}

export function useFactory() {
  const ctx = useContext(FactoryContext);
  if (!ctx) throw new Error('useFactory must be used inside FactoryProvider');
  return ctx;
}
